/**
 * Role Store - Zustand
 *
 * Centralized state management for user roles
 *
 * Features:
 * - CRUD operations (Create, Read, Update, Delete)
 * - Real-time search and filtering
 * - Pagination support
 * - Sorting capabilities
 * - Loading and error states
 * - Optimistic UI updates
 * - Toast notifications
 * - Clone functionality
 * - Permission management
 *
 * Backend API: /api/roles
 *
 * @module useRoleStore
 */

import { create } from 'zustand'
import { roleService } from '@services/roleService'
import toast from 'react-hot-toast'

/**
 * Role Store
 *
 * State Management for User Roles
 */
export const useRoleStore = create((set, get) => ({
	// ============================================================================
	// STATE
	// ============================================================================

	/**
	 * List of all roles
	 * @type {Array<Object>}
	 */
	roles: [],

	/**
	 * Currently selected role for viewing/editing
	 * @type {Object|null}
	 */
	selectedRole: null,

	/**
	 * Loading state for async operations
	 * @type {boolean}
	 */
	loading: false,

	/**
	 * Error message if operation fails
	 * @type {string|null}
	 */
	error: null,

	/**
	 * Filter and search parameters
	 * @type {Object}
	 */
	filters: {
		search: '', // Search by name
		type: 'all', // all, system, custom
		status: 'all', // all, active, archived
		company_id: null, // Filter by company
	},

	/**
	 * Pagination configuration
	 * @type {Object}
	 */
	pagination: {
		page: 1, // Current page
		limit: 10, // Items per page
		total: 0, // Total number of items
		totalPages: 0, // Total number of pages
	},

	/**
	 * Statistics about roles
	 * @type {Object}
	 */
	stats: {
		total: 0,
		system: 0,
		custom: 0,
		active: 0,
		archived: 0,
	},

	// ============================================================================
	// ACTIONS - READ OPERATIONS
	// ============================================================================

	/**
	 * Fetch all roles with current filters
	 *
	 * @returns {Promise<void>}
	 */
	fetchRoles: async () => {
		set({ loading: true, error: null })

		try {
			const { filters, pagination } = get()

			// Build query parameters
			const params = {
				page: pagination.page,
				limit: pagination.limit,
			}

			if (filters.search) {
				params.search = filters.search
			}

			if (filters.type !== 'all') {
				params.type = filters.type
			}

			if (filters.status !== 'all') {
				params.status = filters.status
			}

			if (filters.company_id) {
				params.company_id = filters.company_id
			}

			// Make API request
			const response = await roleService.getAll(params)

			// Backend returns { status, message, data: [...], count, meta: { page, limit, total, total_pages } }
			const roles = response.data || []
			const meta = response.meta || {}
			const total = meta.total || response.count || roles.length
			const totalPages = meta.total_pages || Math.ceil(total / pagination.limit)

			// Update state with response data
			set({
				roles: roles,
				pagination: {
					...pagination,
					page: meta.page || pagination.page,
					total: total,
					totalPages: totalPages,
				},
				loading: false,
			})

			// Update statistics
			get().calculateStats()
		} catch (error) {
			console.error('Failed to fetch roles:', error)
			set({
				error: error.message || 'Failed to load roles',
				loading: false,
			})
			toast.error('Failed to load roles')
		}
	},

	/**
	 * Fetch single role by ID
	 *
	 * @param {number} id - Role ID
	 * @returns {Promise<Object>} Role data
	 */
	fetchRoleById: async (id) => {
		set({ loading: true, error: null })

		try {
			const response = await roleService.getById(id)
			const role = response.data || response

			set({
				selectedRole: role,
				loading: false,
			})

			return role
		} catch (error) {
			console.error('Failed to fetch role:', error)
			set({
				error: error.message || 'Role not found',
				loading: false,
			})
			toast.error('Role not found')
			throw error
		}
	},

	// ============================================================================
	// ACTIONS - CREATE OPERATIONS
	// ============================================================================

	/**
	 * Create new custom role
	 *
	 * @param {Object} data - Role data
	 * @returns {Promise<Object>} Created role
	 */
	createRole: async (data) => {
		set({ loading: true, error: null })

		try {
			const response = await roleService.create(data)
			const newRole = response.data || response

			// Optimistic UI update - add to beginning of list
			set((state) => ({
				roles: [newRole, ...state.roles],
				pagination: {
					...state.pagination,
					total: state.pagination.total + 1,
				},
				loading: false,
			}))

			toast.success('Role created successfully!')

			// Update statistics
			get().calculateStats()

			return newRole
		} catch (error) {
			console.error('Failed to create role:', error)

			const errorMessage = error.response?.data?.message || error.message || 'Failed to create role'

			set({
				error: errorMessage,
				loading: false,
			})

			toast.error(errorMessage)
			throw error
		}
	},

	// ============================================================================
	// ACTIONS - UPDATE OPERATIONS
	// ============================================================================

	/**
	 * Update existing role
	 *
	 * @param {number} id - Role ID
	 * @param {Object} data - Updated role data
	 * @returns {Promise<Object>} Updated role
	 */
	updateRole: async (id, data) => {
		set({ loading: true, error: null })

		try {
			const response = await roleService.update(id, data)
			const updatedRole = response.data || response

			// Optimistic UI update - replace in list
			set((state) => ({
				roles: state.roles.map((role) => (role.id == id ? updatedRole : role)),
				selectedRole: state.selectedRole?.id == id ? updatedRole : state.selectedRole,
				loading: false,
			}))

			toast.success('Role updated successfully!')

			// Update statistics
			get().calculateStats()

			return updatedRole
		} catch (error) {
			console.error('Failed to update role:', error)

			// Extract detailed error message
			let errorMessage = 'Failed to update role'

			if (error.response?.data) {
				const responseData = error.response.data

				if (responseData.messages) {
					if (typeof responseData.messages === 'object') {
						const errors = Object.values(responseData.messages).filter(
							(msg) => typeof msg === 'string'
						)
						if (errors.length > 0) {
							errorMessage = errors.join(', ')
						}
					} else if (typeof responseData.messages === 'string') {
						errorMessage = responseData.messages
					}
				} else if (responseData.message) {
					errorMessage = responseData.message
				}
			} else if (error.message) {
				errorMessage = error.message
			}

			set({
				error: errorMessage,
				loading: false,
			})

			toast.error(errorMessage)
			throw error
		}
	},

	// ============================================================================
	// ACTIONS - DELETE & RESTORE OPERATIONS
	// ============================================================================

	/**
	 * Delete role (soft delete)
	 *
	 * @param {number} id - Role ID
	 * @returns {Promise<void>}
	 */
	deleteRole: async (id) => {
		set({ loading: true, error: null })

		try {
			await roleService.delete(id)

			// Optimistic UI update - mark as archived
			set((state) => ({
				roles: state.roles.map((role) =>
					role.id == id ? { ...role, status: 'archived', deleted_at: new Date().toISOString() } : role
				),
				selectedRole: state.selectedRole?.id == id ? null : state.selectedRole,
				loading: false,
			}))

			toast.success('Role archived successfully!')

			// Update statistics
			get().calculateStats()

			// Refresh to apply filters
			setTimeout(() => get().fetchRoles(), 100)
		} catch (error) {
			console.error('Failed to delete role:', error)

			const errorMessage = error.response?.data?.message || error.message || 'Failed to delete role'

			set({
				error: errorMessage,
				loading: false,
			})

			toast.error(errorMessage)
			throw error
		}
	},

	/**
	 * Restore archived role
	 *
	 * @param {number} id - Role ID
	 * @returns {Promise<void>}
	 */
	restoreRole: async (id) => {
		set({ loading: true, error: null })

		try {
			const response = await roleService.restore(id)
			const restored = response.data || response

			// Optimistic UI update - update existing role in list
			set((state) => ({
				roles: state.roles.map((role) =>
					role.id == id ? { ...role, status: 'active', deleted_at: null } : role
				),
				selectedRole: state.selectedRole?.id == id ? null : state.selectedRole,
				loading: false,
			}))

			toast.success('Role restored successfully!')

			// Update statistics
			get().calculateStats()

			// Refresh to apply filters
			setTimeout(() => get().fetchRoles(), 100)
		} catch (error) {
			console.error('Failed to restore role:', error)

			const errorMessage = error.response?.data?.message || error.message || 'Failed to restore role'

			set({
				error: errorMessage,
				loading: false,
			})

			toast.error(errorMessage)
			throw error
		}
	},

	/**
	 * Clone role
	 *
	 * @param {number} id - Role ID to clone
	 * @param {Object} data - Clone customization data
	 * @returns {Promise<Object>} Cloned role
	 */
	cloneRole: async (id, data) => {
		set({ loading: true, error: null })

		try {
			const response = await roleService.clone(id, data)
			const clonedRole = response.data || response

			// Add to beginning of list
			set((state) => ({
				roles: [clonedRole, ...state.roles],
				pagination: {
					...state.pagination,
					total: state.pagination.total + 1,
				},
				loading: false,
			}))

			toast.success('Role cloned successfully!')

			// Update statistics
			get().calculateStats()

			return clonedRole
		} catch (error) {
			console.error('Failed to clone role:', error)

			const errorMessage = error.response?.data?.message || error.message || 'Failed to clone role'

			set({
				error: errorMessage,
				loading: false,
			})

			toast.error(errorMessage)
			throw error
		}
	},

	// ============================================================================
	// ACTIONS - FILTER & SEARCH
	// ============================================================================

	/**
	 * Update search/filter parameters and refresh data
	 *
	 * @param {Object} newFilters - New filter values
	 */
	setFilters: (newFilters) => {
		set((state) => ({
			filters: { ...state.filters, ...newFilters },
			pagination: { ...state.pagination, page: 1 }, // Reset to page 1 on filter change
		}))

		// Fetch with new filters
		get().fetchRoles()
	},

	/**
	 * Update search term
	 *
	 * @param {string} searchTerm - Search query
	 */
	setSearch: (searchTerm) => {
		get().setFilters({ search: searchTerm })
	},

	/**
	 * Update type filter
	 *
	 * @param {string} type - Type filter (all, system, custom)
	 */
	setTypeFilter: (type) => {
		get().setFilters({ type })
	},

	/**
	 * Update status filter
	 *
	 * @param {string} status - Status filter (all, active, archived)
	 */
	setStatusFilter: (status) => {
		get().setFilters({ status })
	},

	/**
	 * Clear all filters
	 */
	clearFilters: () => {
		set({
			filters: {
				search: '',
				type: 'all',
				status: 'all',
				company_id: null,
			},
			pagination: {
				...get().pagination,
				page: 1,
			},
		})

		get().fetchRoles()
	},

	// ============================================================================
	// ACTIONS - PAGINATION
	// ============================================================================

	/**
	 * Go to specific page
	 *
	 * @param {number} page - Page number
	 */
	setPage: (page) => {
		set((state) => ({
			pagination: { ...state.pagination, page },
		}))

		get().fetchRoles()
	},

	/**
	 * Go to next page
	 */
	nextPage: () => {
		const { pagination } = get()
		if (pagination.page < pagination.totalPages) {
			get().setPage(pagination.page + 1)
		}
	},

	/**
	 * Go to previous page
	 */
	prevPage: () => {
		const { pagination } = get()
		if (pagination.page > 1) {
			get().setPage(pagination.page - 1)
		}
	},

	// ============================================================================
	// ACTIONS - UTILITIES
	// ============================================================================

	/**
	 * Calculate statistics from current roles
	 */
	calculateStats: () => {
		const { roles } = get()

		const stats = {
			total: roles.length,
			system: roles.filter((role) => role.is_system_default === true).length,
			custom: roles.filter((role) => role.is_system_default === false).length,
			active: roles.filter((role) => role.status === 'active').length,
			archived: roles.filter((role) => role.status === 'archived' || role.deleted_at).length,
		}

		set({ stats })
	},

	/**
	 * Select role for viewing/editing
	 *
	 * @param {Object|null} role - Role to select
	 */
	selectRole: (role) => {
		set({ selectedRole: role })
	},

	/**
	 * Clear selected role
	 */
	clearSelection: () => {
		set({ selectedRole: null })
	},

	/**
	 * Reset entire store to initial state
	 */
	reset: () => {
		set({
			roles: [],
			selectedRole: null,
			loading: false,
			error: null,
			filters: {
				search: '',
				type: 'all',
				status: 'all',
				company_id: null,
			},
			pagination: {
				page: 1,
				limit: 10,
				total: 0,
				totalPages: 0,
			},
			stats: {
				total: 0,
				system: 0,
				custom: 0,
				active: 0,
				archived: 0,
			},
		})
	},
}))

export default useRoleStore
